import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InputIcon from "@mui/icons-material/Input";

import { PERMISSIONS } from "../../constants";
import {
  createTrdDependence, downloadTrdXlsx, importExistingTrd, listTrdDependencies,
} from "../../services/backend/TrdService";
import { STATUS_COLORS, STATUS_LABELS, trdError } from "./trdLabels";

// Lista de dependencias de la TRD: qué versión rige, si hay un borrador abierto
// y cuántas subseries aún no tienen sus parámetros definidos.
export default function TrdList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const can = (p) => Array.isArray(user?.permissions) && user.permissions.includes(p);

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { body, error: err } = await listTrdDependencies();
    if (err) { setError(trdError(err)); setRows([]); return; }
    setError(null);
    setRows(body.dependencies);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (evt) => {
    evt.preventDefault();
    setBusy(true);
    const { body, error: err } = await createTrdDependence(form.code, form.name);
    setBusy(false);
    if (err) { setError(trdError(err)); return; }
    setCreating(false);
    setForm({ code: "", name: "" });
    navigate(`/trd/${encodeURIComponent(body.version.dependenceCode)}`);
  };

  const handleImport = async () => {
    setBusy(true);
    const { body, error: err } = await importExistingTrd();
    setBusy(false);
    if (err) { setError(trdError(err)); return; }
    const invalid = body.invalid.length
      ? ` No se pudieron importar ${body.invalid.length}: ${body.invalid.map((i) => `${i.docId} (${i.error})`).join(", ")}.`
      : "";
    setNotice(`Importadas ${body.imported.length}, ya existentes ${body.skipped.length}.${invalid}`);
    load();
  };

  const handleExport = async () => {
    const err = await downloadTrdXlsx();
    if (err) setError(trdError(err));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} spacing={2}>
        <Typography variant="h4">Tabla de Retención Documental</Typography>
        <Stack direction="row" spacing={1}>
          {can(PERMISSIONS.TRD.EXPORT)
            && <Button startIcon={<FileDownloadIcon />} onClick={handleExport}>Exportar vigente</Button>}
          {can(PERMISSIONS.TRD.CREATE)
            && <Button startIcon={<InputIcon />} onClick={handleImport} disabled={busy}>Importar TRD existente</Button>}
          {can(PERMISSIONS.TRD.CREATE)
            && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>Nueva dependencia</Button>}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Dependencias, series, subseries y tipos documentales con su retención y disposición final.
        Lo vigente es lo que ofrece el módulo de Expedientes.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {rows === null
        ? <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        : rows.length === 0
          ? <Alert severity="info">Aún no hay dependencias. Crea una o importa la TRD existente.</Alert>
          : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Dependencia</TableCell>
                    <TableCell>Vigente</TableCell>
                    <TableCell>Borrador</TableCell>
                    <TableCell>Parámetros pendientes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const pending = (r.current?.pendingSubseries || 0);
                    return (
                      <TableRow
                        key={r.dependenceCode}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/trd/${encodeURIComponent(r.dependenceCode)}`)}
                      >
                        <TableCell>{r.dependenceCode}</TableCell>
                        <TableCell>
                          {r.name}
                          {!r.active && <Chip size="small" label="Inactiva" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>
                          {r.current
                            ? <Chip size="small" color={STATUS_COLORS.VIGENTE} label={`v${r.current.version}`} />
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {r.draft
                            ? <Chip size="small" color={STATUS_COLORS.BORRADOR} label={`${STATUS_LABELS.BORRADOR} v${r.draft.version}`} />
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {pending > 0
                            ? (
                              <Tooltip title="Subseries vigentes sin soporte, retención, disposición final o tipos documentales">
                                <Chip size="small" color="warning" label={`${pending} subserie${pending === 1 ? "" : "s"}`} />
                              </Tooltip>
                            )
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleCreate}>
          <DialogTitle>Nueva dependencia</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus required fullWidth margin="dense" label="Código"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
            <TextField
              required fullWidth margin="dense" label="Dependencia (oficina productora)"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreating(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={busy}>Crear</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
}
